'use strict';
const _ = require('lodash');
const BigNumber = require('bignumber.js');
const moment = require('moment');
const DATE_FORMAT = [moment.ISO_8601, 'l', 'll', 'L', 'LL'];
const DATE_TIME_FORMAT = [moment.ISO_8601, 'lll', 'LLL', 'YYYY-MM-DD H:mm', 'YYYY-MM-DD HH:mm', 'DD-MM-YYYY H:mm', 'DD-MM-YYYY HH:mm'];
const IBAN = require('iban');
const parsers = require('./parsers');
const isBSN = require('./helpers/isBSN');
const isPhoneNumber = require('./helpers/isPhoneNumber');
const isEmailAddress = require('./helpers/isEmailAddress');
const isPostalCode = require('./helpers/isPostalCode');
const isUrl = require('./helpers/isUrl');
const isSepaCountryIban = require('./helpers/isSepaCountryIban');

var required = function (data, property, config, rule) {
  var value = _.get(data, property);

  var valid = value !== undefined && value !== null;

  if (_.isString(value)) {
    return valid && value.length > 0;
  }

  if (!_.isNil(value) && rule.baseType === 'datetime') {
    return !_.isEmpty(value.time) && !_.isEmpty(value.date);
  }

  if (!_.isNil(value) && /[hH]tml_txt$/.test(property) && _.isFunction(value.getCurrentContent)) {
    const text = value.getCurrentContent().getPlainText();
    return !_.isEmpty(text);
  }

  return valid;
};

var pattern = function (data, property, config) {
  var value = _.get(data, property);

  if (value === null || value === undefined) {
    return true;
  }

  if (_.isString(value) && value.length === 0) {
    return true;
  }

  var pat = new RegExp(config.pattern, config.options);

  return pat.test(value);
};

module.exports = {
  'required': required,
  'conditionalRequired': function (data, property, config, rule) {

    var configs = config;

    if (!_.isArray(config)) {
      configs = [config];
    }

    var valid = true;

    _.each(configs, function (conf) {

      var dependencies = config.dependsOn;

      if (!_.isArray(dependencies)) {
        dependencies = [dependencies];
      }

      var conditionIsMet = false;

      _.each(dependencies, function (dependsOn) {
        var conditionValue = _.get(data, dependsOn);
        conditionIsMet = conditionIsMet || (conditionValue !== undefined && conditionValue !== null);
      });

      if (conditionIsMet) {
        valid = required(data, property, config, rule);

        if (!valid) {
          return false;
        }
      }
    });

    return valid;
  },
  'birthDateMustBeInThePast': function (data, property, config) {
    var birthDate = _.get(data, config.birthDate);

    if (!birthDate) {
      return true;
    }

    var m = new moment(birthDate, DATE_FORMAT, true);
    return moment().isAfter(m);
  },
  'length': function (data, property, config) {
    var max = config['max'];
    var min = config['min'];

    if (!max) {
      max = 0;
    }

    if (!min) {
      min = 0;
    }

    var value = _.get(data, property);

    if (!value) {
      return true;
    }

    if (!_.isString(value)) {
      return false;
    }

    return (value.length >= min && value.length <= max);
  },
  'pattern': pattern,
  'phone': function (data, property) {
    const val = _.get(data, property);
    if (_.isNil(val) || _.trim(val) === '') {
      return true;
    }

    return isPhoneNumber(val);
  },
  'email': function (data, property) {
    var value = _.get(data, property);

    if (value === null || value === undefined) {
      return true;
    }

    if (_.isString(value) && value.length === 0) {
      return true;
    }

    return isEmailAddress(value);
  },
  'youngerThan': function (data, property, config) {
    var birthDate = _.get(data, config.birthDate);
    var age = _.get(config, 'age');

    if (!birthDate) {
      return true;
    }

    if (!age) {
      return true;
    }

    var m = new moment(birthDate, DATE_FORMAT);
    var diff = moment().diff(m, 'years');
    return (diff >= age);
  },
  'olderThan': function (data, property, config) {
    var birthDate = _.get(data, config.birthDate);
    var age = _.get(config, 'age');

    if (!birthDate) {
      return true;
    }

    if (!age) {
      return true;
    }

    birthDate = new moment(birthDate, DATE_FORMAT);
    return (moment().diff(birthDate, 'years') < age);
  },
  'range': function (data, property, config) {
    var minValue = _.get(config, ['min']);
    var maxValue = _.get(config, ['max']);

    if (!minValue) {
      return;
    }

    if (!maxValue) {
      return;
    }

    var min = new BigNumber(minValue);
    var max = new BigNumber(maxValue);

    var value = _.get(data, property);

    var parsedValue;

    // return true if there is no input, else parse the value for comparison
    if (!value) {
      return true;
    } else {
      parsedValue = new BigNumber(_.get(data, property));
    }

    if (parsedValue.isNaN()) {
      return false;
    }

    if (parsedValue.isLessThan(min)) {
      return false;
    }

    if (parsedValue.isGreaterThan(max)) {
      return false;
    }

    return true;
  },
  'custom': function (data, property, config) {
    if (_.isFunction(config)) {
      return config(data, property);
    }

    return false;
  },
  'after': function (data, property, config) {
    var value = _.get(data, property);
    var start = _.get(data, _.get(config, 'start'));

    if (_.isNil(value) || _.isNil(start)) {
      return true;
    }

    var dateValue = new moment(value, DATE_FORMAT, true);
    var startDate = new moment(start, DATE_FORMAT, true);

    if (!parsers.date(startDate).valid) {
      return true;
    }

    return dateValue.isAfter(startDate, 'day');
  },
  'afterDateTime': function (data, property, config) {
    var value = _.get(data, property);
    var start = _.get(data, _.get(config, 'start'));

    if (_.isEmpty(value) || _.isEmpty(start)) {
      return true;
    }

    var dateValue = new moment(`${value.date} ${value.time}`, DATE_TIME_FORMAT);
    var startDate = new moment(`${start.date} ${start.time}`, DATE_TIME_FORMAT);

    if (!parsers.datetime(startDate).valid) {
      return true;
    }

    return dateValue.isAfter(startDate, 'minute');
  },
  'before': function (data, property, config) {
    var value = _.get(data, property);
    var start = _.get(data, _.get(config, 'start'));

    if (_.isNil(value) || _.isNil(start)) {
      return true;
    }

    var dateValue = new moment(value, DATE_FORMAT);
    var startDate = new moment(start, DATE_FORMAT);

    if (!parsers.date(startDate).valid) {
      return true;
    }

    return dateValue.isBefore(startDate, 'day');
  },
  'afterOrEqual': function (data, property, config) {
    var value = _.get(data, property);
    var start = _.get(data, _.get(config, 'start'));

    if (_.isNil(value) || _.isNil(start)) {
      return true;
    }

    var dateValue = new moment(value, DATE_FORMAT);
    var startDate = new moment(start, DATE_FORMAT);

    if (!parsers.date(startDate).valid) {
      return true;
    }

    return dateValue.isAfter(startDate, 'day') || dateValue.isSame(startDate, 'day');
  },
  'beforeOrEqual': function (data, property, config) {
    var value = _.get(data, property);
    var start = _.get(data, _.get(config, 'start'));

    if (_.isNil(value) || _.isNil(start)) {
      return true;
    }

    var dateValue = new moment(value, DATE_FORMAT);
    var startDate = new moment(start, DATE_FORMAT);

    if (!parsers.date(startDate).valid) {
      return true;
    }

    return dateValue.isSameOrBefore(startDate, 'day');
  },
  'pastDateOrToday': function (data, property, config) {
    var value = _.get(data, property);
    var now = new moment();

    if (_.isNil(value)) {
      return true;
    }

    var dateValue = new moment(value, DATE_FORMAT);

    if (!parsers.date(dateValue).valid) {
      return true;
    }

    return dateValue.isBefore(now, 'day') || dateValue.isSame(now, 'day');
  },
  'pastDate': function (data, property, config) {
    var value = _.get(data, property);
    var now = new moment();

    if (_.isNil(value)) {
      return true;
    }

    var dateValue = new moment(value, DATE_FORMAT);

    if (!parsers.date(dateValue).valid) {
      return true;
    }

    return dateValue.isBefore(now, 'day');
  },
  'futureDateOrToday': function (data, property, config) {
    var value = _.get(data, property);
    var now = new moment();

    if (_.isNil(value)) {
      return true;
    }

    var dateValue = new moment(value, DATE_FORMAT);

    if (!parsers.date(dateValue).valid) {
      return true;
    }

    return dateValue.isAfter(now, 'day') || dateValue.isSame(now, 'day');
  },
  'futureDate': function (data, property, config) {
    var value = _.get(data, property);
    var now = new moment();

    if (_.isNil(value)) {
      return true;
    }

    var dateValue = new moment(value, DATE_FORMAT);

    if (!parsers.date(dateValue).valid) {
      return true;
    }

    return dateValue.isAfter(now, 'day');
  },
  'iban': function (data, property) {
    var value = _.get(data, property);

    if (_.isNil(value) || _.trim(value) === '') {
      return true;
    }

    return IBAN.isValid(value);
  },
  'iban-nl': function (data, property) {
    var value = _.get(data, property);

    if (_.isNil(value)) {
      return true;
    }

    if (!IBAN.isValid(value)) {
      return false;
    }

    return IBAN.isValidBBAN('NL', IBAN.toBBAN(value));
  },
  'iban-sepa': function (data, property) {
    var value = _.get(data, property);

    if (_.isNil(value)) return true;
    if (!IBAN.isValid(value)) return false;

    return isSepaCountryIban(value);
  },
  'sepa': function (data, property) {
    var value = _.get(data, property);

    if (_.isNil(value)) {
      return true;
    }

    return !(/[^\\A-Z0-9\/\-\?\:\(\)\.\,\'\+\s]/gi.test(value));
  },
  'bsn': function (data, property) {
    const value = _.get(data, property);
    return isBSN(value);
  },
  postalCode: function (data, property) {
    const value = _.get(data, property);
    if (_.isNil(value)) {
      return true;
    }

    return isPostalCode(value);
  },
  url: function (data, property) {
    const value = _.get(data, property);
    return isUrl(value);
  }
};
